import {
  Environment,
  Network,
  RecordSource,
  Store,
  Observable,
} from 'relay-runtime';
import type {
  RequestParameters,
  Variables,
  GraphQLResponse,
  Observer,
  CacheConfig,
} from 'relay-runtime';
import { type Operation as ApolloOperation } from '@apollo/client/link/core';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';

import { parse } from 'graphql';

import createRelaySubscriptionHandler from 'graphql-ruby-client/subscriptions/createRelaySubscriptionHandler';
import {
  UPLOAD_PROGRESS_METADATA_KEY,
  fetchWithUploadProgress,
} from './fetchWithUploadProgress';

const GRAPHQL_ENDPOINT = '/graphql';

const uploadLink = createUploadLink({
  uri: GRAPHQL_ENDPOINT,
  credentials: 'same-origin',
  headers: {
    'X-CSRF-Token': AUTH_TOKEN,
    Accept:
      'application/graphql-response+json; charset=utf-8, application/json; charset=utf-8',
    'Content-Type': 'application/json',
  },
  fetch: fetchWithUploadProgress,
});

const fetchFn = (
  operation: RequestParameters,
  variables: Variables,
  cacheConfig: CacheConfig
): Observable<GraphQLResponse> => {
  return Observable.create((observer: Observer<GraphQLResponse>) => {
    const { text: query } = operation;
    let context: Record<string, unknown> = {};

    if (query === null) {
      observer.error?.(new Error('Query is null'));
      return;
    }

    if (!navigator.onLine) {
      observer.error?.(new Error('There is no internet access'));
      return;
    }

    const operationCompat = {
      query: parse(query),
      variables,
      operationName: operation.name,
      extensions: {},
      getContext: () => context,
      setContext: (newContext: Record<string, unknown>) => {
        context = { ...context, ...newContext };
      },
    } as ApolloOperation;

    const onUploadProgress =
      cacheConfig.metadata?.[UPLOAD_PROGRESS_METADATA_KEY];
    if (typeof onUploadProgress === 'function') {
      operationCompat.setContext({
        fetchOptions: {
          onUploadProgress,
        },
      });
    }

    const uploadObservable = uploadLink.request(operationCompat);

    if (uploadObservable === null) {
      observer.error?.(new Error('Upload link did not return an observable'));
      return;
    }

    const subscription = uploadObservable.subscribe({
      next: (response) => {
        if (typeof TEST_CLUSTER_MODE !== 'undefined' && TEST_CLUSTER_MODE) {
          console.log('GraphQL response', JSON.stringify(response));
        }
        observer.next?.(response as GraphQLResponse);
      },
      error: (err) => {
        if (err.statusCode === 401) {
          window.location.href = '/login';
        } else {
          observer.error?.(err);
        }
      },
      complete: () => observer.complete?.(),
    });

    return () => subscription.unsubscribe();
  });
};

const actionCable = window.App.cable;

if (actionCable == null) {
  throw new Error('Action Cable consumer is not initialized');
}

const baseSubscriptionFn = createRelaySubscriptionHandler({
  channelName: 'GraphQLChannel',
  cable: actionCable,
});

const loggedSubscriptionFn = (
  operation: RequestParameters,
  variables: Variables
): Observable<GraphQLResponse> => {
  const source = baseSubscriptionFn(
    operation,
    variables
  ) as Observable<GraphQLResponse>;

  return Observable.create<GraphQLResponse>((sink) => {
    const disposable = source.subscribe({
      next: (payload) => {
        if (
          typeof TEST_CLUSTER_MODE !== 'undefined' &&
          /* eslint-disable camelcase */ TEST_CLUSTER_MODE
        ) {
          console.log(
            `Relay subscription: ${operation.name}`,
            JSON.stringify(payload, null, 2)
          );
        }
        sink.next(payload);
      },
      error: (err: Error) => sink.error(err),
      complete: () => sink.complete(),
    });

    return () => {
      disposable.unsubscribe?.();
    };
  });
};

export function createRelayEnvironment(subscriptionFn = loggedSubscriptionFn) {
  return new Environment({
    network: Network.create(fetchFn, subscriptionFn),
    store: new Store(new RecordSource()),
  });
}

export const RelayEnvironment = createRelayEnvironment();
